import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, CheckCircle2, AlertTriangle } from 'lucide-react';
interface TrendItem {
  type: string;
  count: number;
  change: number;
}
interface TrendSectionProps {
  improved: TrendItem[];
  stable: TrendItem[];
  worsened: TrendItem[];
}
export function TrendSection({
  improved,
  stable,
  worsened
}: TrendSectionProps) {
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const ColumnHeader = ({
    title,
    icon: Icon,
    colorClass,
    bgClass
  }: {
    title: string;
    icon: any;
    colorClass: string;
    bgClass: string;
  }) => <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/5" data-id="element-1211" data-mp="qvm576ofl6r9">
      <div className={`p-2.5 rounded-xl ${bgClass} ${colorClass}`} data-id="element-1212" data-mp="qxozw40j3o2b">
        <Icon size={20} strokeWidth={2.5} data-id="element-1213" data-mp="bpcc8xmbouoq" />
      </div>
      <h3 className={`text-lg font-bold ${colorClass}`} data-id="element-1214" data-mp="gmuk0kolt3v5">{title}</h3>
    </div>;
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-7xl mx-auto px-4" data-id="element-1215" data-mp="lm73qwk6huti">
      {/* Improved Column */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-50px'
    }} className="bg-gray-900/40 backdrop-blur-sm rounded-3xl p-6 border border-white/5" data-id="element-1216" data-mp="qd5p4ak0xj3m">
        <ColumnHeader title="Improved" icon={TrendingDown} colorClass="text-emerald-400" bgClass="bg-emerald-500/10" data-id="element-1217" data-mp="14cou4uhjgebp" />

        <div className="space-y-3" data-id="element-1218" data-mp="19b03l43x8mte">
          {improved.length > 0 ? improved.map(pattern => <motion.div key={pattern.type} variants={item} className="group flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5 hover:bg-gray-800/60 hover:border-emerald-500/20 transition-all duration-200" data-id="element-1219" data-mp="366eglvlcquka">
                <div className="flex items-center gap-3" data-id="element-1220" data-mp="3d0mp6g03gu60">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1221" data-mp="3d27lob5yyn6i" />
                  <span className="text-gray-200 font-medium group-hover:text-white transition-colors" data-id="element-1222" data-mp="380i8aov9xxf9">
                    {pattern.type}
                  </span>
                </div>
                <span className="text-emerald-400 text-sm font-bold bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/10" data-id="element-1223" data-mp="3h2riteqjlqsn">
                  -{Math.abs(pattern.change)}%
                </span>
              </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-white/5 rounded-xl" data-id="element-1224" data-mp="3qudpcxrxr1p2">
              <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center mb-3 text-gray-600" data-id="element-1225" data-mp="4lm96ke4pbyn">
                <Minus size={20} data-id="element-1226" data-mp="naet40d1242g" />
              </div>
              <p className="text-gray-500 text-sm font-medium" data-id="element-1227" data-mp="s7y852hjukvg">
                No improvements yet
              </p>
            </div>}
        </div>
      </motion.div>

      {/* Stable Column */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-50px'
    }} className="bg-gray-900/40 backdrop-blur-sm rounded-3xl p-6 border border-white/5" data-id="element-1228" data-mp="j79ui8w2imj3">
        <ColumnHeader title="Stable" icon={Minus} colorClass="text-amber-400" bgClass="bg-amber-500/10" data-id="element-1229" data-mp="9b6v3aslp1zu" />

        <div className="space-y-3" data-id="element-1230" data-mp="3vpq5yxid9z1k">
          {stable.length > 0 ? stable.map(pattern => <motion.div key={pattern.type} variants={item} className="group flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5 hover:bg-gray-800/60 hover:border-amber-500/20 transition-all duration-200" data-id="element-1231" data-mp="3hpnr7k5cvlqj">
                <div className="flex items-center gap-3" data-id="element-1232" data-mp="ovguyepahw4a">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" data-id="element-1233" data-mp="k35jqfp3mnwf" />
                  <span className="text-gray-200 font-medium group-hover:text-white transition-colors" data-id="element-1234" data-mp="f4vn00nq7f8p">
                    {pattern.type}
                  </span>
                </div>
                <span className="text-amber-400 text-sm font-bold bg-amber-500/10 px-2.5 py-1 rounded-lg border border-amber-500/10" data-id="element-1235" data-mp="a3eoucwmm6vt">
                  ~
                </span>
              </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-white/5 rounded-xl" data-id="element-1236" data-mp="3sbxbr87ni61c">
              <p className="text-gray-500 text-sm font-medium" data-id="element-1237" data-mp="1viluzihyunnm">
                No stable patterns
              </p>
            </div>}
        </div>
      </motion.div>

      {/* Worsened Column */}
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-50px'
    }} className="bg-gray-900/40 backdrop-blur-sm rounded-3xl p-6 border border-white/5" data-id="element-1238" data-mp="1y3a2l7vknum4">
        <ColumnHeader title="Needs Attention" icon={AlertTriangle} colorClass="text-red-400" bgClass="bg-red-500/10" data-id="element-1239" data-mp="2cr4wgrlbg50s" />

        <div className="space-y-3" data-id="element-1240" data-mp="1k38zrmvqyd40">
          {worsened.length > 0 ? worsened.map(pattern => <motion.div key={pattern.type} variants={item} className="group flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5 hover:bg-gray-800/60 hover:border-red-500/20 transition-all duration-200" data-id="element-1241" data-mp="1p1sofv1q6302">
                <div className="flex items-center gap-3" data-id="element-1242" data-mp="34ikstdcod647">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-1243" data-mp="15eucp2oba5o3" />
                  <span className="text-gray-200 font-medium group-hover:text-white transition-colors" data-id="element-1244" data-mp="15d18rv531zjs">
                    {pattern.type}
                  </span>
                </div>
                <span className="text-red-400 text-sm font-bold bg-red-500/10 px-2.5 py-1 rounded-lg border border-red-500/10" data-id="element-1245" data-mp="1ef0pemc6r1m7">
                  +{pattern.change}%
                </span>
              </motion.div>) : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-emerald-500/20 bg-emerald-500/5 rounded-xl" data-id="element-1246" data-mp="hp9r0c0rwub4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3 text-emerald-400" data-id="element-1247" data-mp="vhzgiqfighcz">
                <CheckCircle2 size={20} data-id="element-1248" data-mp="30dp6wpb8diy" />
              </div>
              <p className="text-emerald-400 text-sm font-bold" data-id="element-1249" data-mp="2ysv186wqwlm">Great job!</p>
              <p className="text-emerald-500/70 text-xs mt-1" data-id="element-1250" data-mp="3u5n8y6mpnvk4">
                No new concerns detected
              </p>
            </div>}
        </div>
      </motion.div>
    </div>;
}